<template>
  <div :id="modalId" class="product-modal" uk-modal>
    <div class="uk-modal-dialog uk-margin-auto-vertical">
      <button class="uk-modal-close-default" type="button" uk-close></button>

      <div class="uk-modal-body">
        <div class="row">
          <div v-if="imageUrl || subImage" 
               :class="imageContainerClass">
            <!-- メイン画像（imageUrl） -->
            <div v-if="imageUrl" class="image-wrapper">
              <img :src="imageUrl" :title="name" :alt="name" />
            </div>
            
            <!-- 病院用画像（存在する場合のみ横並びで表示） -->
            <div v-if="subImage" class="image-wrapper hospital-image">
              <img :src="subImage" :title="name + '（病院・施設用）'" :alt="name + '（病院・施設用）'" />
            </div>
          </div>
          <div class="column">
            <h2 class="name" v-if="name">{{ name }}</h2>
            <p class="description" v-if="description">{{ description }}</p>
            <p class="features" v-if="productFeatures">{{ productFeatures }}</p>
          </div>
        </div>

        <div class="actions uk-text-center">
          <a v-if="url" class="to-detail-page uk-button uk-button-primary uk-button-small" :href="url" target="_blank" v-wave>
            詳しい商品情報へ
            <i class="material-symbols-sharp" data-icon="chevron_right"></i>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, computed } from 'vue'
  import uikit from 'uikit'

  let modal: uikit.UIkitModalElement | null = null

  const props = defineProps({
    modalId: {
      type: String,
      required: true
    },
    /**
     * 商品名
     */
    name: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * 商品の説明
     */
    description: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * 商品ページURL
     */
    url: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * 商品画像URL
     */
    imageUrl: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * 商品の特長
     */
    productFeatures: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * 施設・病院用商品の画像
     */
    subImage: {
      type: String,
      required: false,
      default: ''
    }
  })

  onMounted(() => {
    modal = uikit.modal(document.getElementById(props.modalId))
  })

  const open = () => {
    if (!modal) {
      return
    }
    modal.show()
  }

  defineExpose({
    open
  })


  /**
   * 画像コンテナのクラスを動的に決定
   */
  const imageContainerClass = computed(() => {
    const hasMainImage = !!props.imageUrl
    const hasSubImage = !!props.subImage
    
    if (hasMainImage && hasSubImage) {
      return ['column', 'images-container', 'multiple-images'] // 2つの画像
    } else {
      return ['column', 'images-container', 'single-image'] // 1つの画像
    }
  })
</script>

<style lang="scss" scoped>
  .uk-modal-dialog {
    position: relative; // 子要素の絶対配置の基準点

    .uk-modal-body {
      padding: 0.75rem;

      .row {
        display: flex;
        flex-direction: column;
        width: 100%;

        .column {
          flex: 1 1 auto;
          padding: 1rem 0.75rem 0;
          white-space: pre-wrap;

          &.images-container {
            display: flex;
            gap: 0.5rem;
            justify-content: center;
            align-items: flex-start;
            overflow: hidden;

            &.single-image {
              flex-direction: column;
              
              .image-wrapper {
                flex: 0 0 auto;
                
                img {
                  max-height: 200px;
                  max-width: 100%;
                  width: auto;
                  height: auto;
                  object-fit: contain;
                }
              }
            }

            &.multiple-images {
              flex-direction: row;
              flex-wrap: wrap;
              margin-right: 1rem; // クローズボタンとの距離を確保
              
              .image-wrapper {
                flex: 0 0 auto;
                max-width: calc(50% - 0.25rem);
                
                img {
                  max-height: 200px;
                  max-width: 100%;
                  width: auto;
                  height: auto;
                  object-fit: contain;
                }
              }
            }
          }

          img {
            max-height: 200px;
            max-width: 100%;
            width: auto;
            height: auto;
            object-fit: contain;
          }

          .name {
            font-size: 1.2rem;
            font-weight: bold;
            margin-top: 0.25rem;
            margin-bottom: 0.25rem;
            color: #666;
          }

          .description {
            font-size: 0.875rem;
            margin: 0.75rem 0;
            line-height: 1.2rem;
          }

          .features {
            font-size: 0.875rem;
            margin: 0.5rem 0;
            line-height: 1.2rem;
            color: #666;
          }

          &:first-child {
            margin: 0 auto;
          }
        }
      }

      .actions {
        padding: 0.5rem 0;

        .correspondence-table-section {
          margin-bottom: 1rem;
          
          .correspondence-table-link-inline {
            color: rgb(236, 108, 0);
            text-decoration: none;
            font-size: 0.875rem;
            font-weight: 500;
            opacity: 0.9;
            transition: opacity 0.25s;
            
            &:hover {
              text-decoration: underline;
              opacity: 1;
            }
          }
        }

        .to-detail-page {
          position: relative;
          color: var(--text-color);
          border: 1px solid var(--text-color);
          border-radius: 1rem;
          padding: 0 2rem 0 1rem;

          i {
            display: inline;
            position: absolute;
            right: 0.5rem;
            top: 50%;
            transform: translateY(-50%);
            opacity: 0.7;
          }

          &:hover {
            border: 1px solid var(--theme-color);
            transition: all .5s;

            i {
              opacity: 1;
              right: .25rem;
              transition: all .5s;
            }
          }
        }
      }
    }

    @media (min-width: 640px) {
      width: 400px;
    }
  }
</style>
